import React from "react";
import { defaultEditorDefns, EditorDefns, TlfViewModeConfig } from "../../utility";
import { ArsTableContent } from "./ArsTableContent";
import { ArsTableContextProvider } from "./ArsTableContextProvider";
import { ArsTableBody, ArsTableJustification, ArsTableRow } from "./interface";

/**
 * Props for the ArsTable component.
 */
export interface ArsTableProps<T> {
  /** ID for the table component */
  id?: string;
  /** Pre-populated table body shown on first render. */
  arsTableBody: ArsTableBody<T>;
  /** Optional flag to disable table interactions. */
  disabled?: boolean;
  /** Editor props used by cell editors */
  editorProps?: EditorDefns<T>;
  /** Horizontal justification of the scaffold table within its container */
  justification?: ArsTableJustification;
  /** Data for the scaffold after ARS table calculation */
  calculatedTableRows: ArsTableRow<T>[] | null;
  /** Optional CSS styles for the sidebar container */
  sidebarStyle?: React.CSSProperties;
  /** Optional sidebar width. */
  sidebarWidth?: string;
  /** Optional container style. */
  style?: React.CSSProperties;
  /** TLF view mode controlling how cell data is displayed. */
  tlfViewMode: TlfViewModeConfig;
  /** Callback invoked when the table body changes */
  updateArsTableBody: (newBody: ArsTableBody<T>, calculatedBody: ArsTableRow<T>[]) => void;
}

/**
 * ArsTable presents a scaffold table with a variable sidebar. Users can drag one or
 * more variables onto header cells and position them before, after, or in place using
 * nine recognised drop zones (edges, corners, and centre).
 *
 * @param props - Component props
 * @param props.id - ID for the table component
 * @param props.arsTableBody - Table body data
 * @param props.disabled - Optional flag to disable table interactions
 * @param props.editorProps - Editor props used by cell editors
 * @param props.justification - Horizontal justification of the scaffold table within its container
 * @param props.calculatedTableRows - Data for the scaffold after ARS table calculation
 * @param props.sidebarStyle - Optional CSS styles for the sidebar container
 * @param props.sidebarWidth - Optional sidebar width
 * @param props.style - Optional container style
 * @param props.tlfViewMode - TLF view mode controlling how cell data is displayed
 * @param props.updateArsTableBody - Callback invoked when the table body changes
 * @returns ArsTable table and draggable variable sidebar
 */
export const ArsTable = <T,>({
  id = "ars-table",
  arsTableBody,
  calculatedTableRows,
  disabled = false,
  editorProps = defaultEditorDefns as EditorDefns<T>,
  justification = ArsTableJustification.Center,
  sidebarStyle,
  style,
  tlfViewMode,
  updateArsTableBody,
}: ArsTableProps<T>): React.ReactElement => {
  return (
    <ArsTableContextProvider
      bodyData={arsTableBody}
      calculatedTableRows={calculatedTableRows}
      updateBodyData={updateArsTableBody}
      settings={{
        id,
        disabled,
        editorProps,
        justification,
        sidebarStyle,
        style,
        tlfViewMode,
      }}
    >
      <ArsTableContent />
    </ArsTableContextProvider>
  );
};

ArsTable.displayName = "ArsTable";
